import React from 'react';
import { Box, Optional } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import AvailabilityStatusBadge from '../shared/AvailabilityStatusBadge';
import ExteriorInteriorColor from '../shared/ExteriorInteriorColor';
import InventoryTrimCardProvider from './InventoryTrimCardProvider';
import UniCard from '../../UniCard';
import formatAriaLabelText from '../utils/formatAriaLabelText';
import getCtaQueryParams from '../utils/getCtaQueryParams';
import getExteriorInteriorColor from '../utils/getExteriorInteriorColor';
import gtmPushHandler from '../utils/gtmPushHandler';
import themeStyles from '../../UniCard/styles/Card.styles';
import useHoverState from '../../ModelCardGrid/useHoverState';
import { EqualHeightElement } from '../../../utils/components/EqualHeight';
import { SellingPriceFlagSource } from '../../UniCard/hooks/useIsSellingPriceFlag';
import { statusBadgeConfig } from '../constants';
const UniCardWrapper = themeStyles.apply(Box, 'UniCardWrapper');
const InventoryTrimCard = ({ trim, inventoryStatusPosition, nextStepUrl }) => {
    var _a, _b, _c, _d;
    const transmission = (_a = trim === null || trim === void 0 ? void 0 : trim.transmissions) === null || _a === void 0 ? void 0 : _a[0];
    const { t } = useTranslation();
    const { exteriorColor, interiorColor } = getExteriorInteriorColor(trim);
    const availabilityStatus = trim.inventoryStatus;
    const araiLabelText = availabilityStatus ? statusBadgeConfig[availabilityStatus].label : '';
    const labelProps = {
        title: `${trim === null || trim === void 0 ? void 0 : trim.modelYear} ${trim === null || trim === void 0 ? void 0 : trim.trimName}`,
        transmissionName: transmission === null || transmission === void 0 ? void 0 : transmission.transmissionName,
        exteriorColorName: exteriorColor === null || exteriorColor === void 0 ? void 0 : exteriorColor.colorName,
        interiorColorName: interiorColor === null || interiorColor === void 0 ? void 0 : interiorColor.colorName,
        inventoryStatus: t(araiLabelText),
    };
    const ariaLabelText = formatAriaLabelText(t, labelProps);
    const { thumbnail, backgroundColor, onMouseEnter, onMouseLeave } = useHoverState({
        primaryThumbnail: (_b = trim === null || trim === void 0 ? void 0 : trim.trimImage) === null || _b === void 0 ? void 0 : _b.primaryThumbnail,
        secondaryThumbnail: (_c = trim === null || trim === void 0 ? void 0 : trim.trimImage) === null || _c === void 0 ? void 0 : _c.secondaryThumbnail,
    });
    const paymentTooltip = {
        year: (trim === null || trim === void 0 ? void 0 : trim.modelYear) || '',
        model: (trim === null || trim === void 0 ? void 0 : trim.modelName) || '',
        trim: (trim === null || trim === void 0 ? void 0 : trim.trimName) || '',
    };
    const cta = {
        gtmEvent: () => gtmPushHandler({
            modelName: trim === null || trim === void 0 ? void 0 : trim.modelName,
            modelYear: trim === null || trim === void 0 ? void 0 : trim.modelYear,
            trimName: trim === null || trim === void 0 ? void 0 : trim.trimName,
            event: 'dealer_inventory_trim_select',
        }),
        text: t('Shared.Common.viewDetailsButton'),
        label: ariaLabelText,
        href: nextStepUrl === null || nextStepUrl === void 0 ? void 0 : nextStepUrl.href,
        linktype: nextStepUrl === null || nextStepUrl === void 0 ? void 0 : nextStepUrl.linktype,
        params: getCtaQueryParams(trim, transmission),
    };
    const cardEvents = {
        onMouseEnter,
        onMouseLeave,
    };
    const paymentDetailsProps = {
        data: trim.paymentData.data && trim.paymentData.data[0],
        error: trim.paymentData.error,
        isFetching: trim.paymentData.isFetching,
    };
    return (
    // Necessary to avoid extremely deep prop-drilling
    // TODO: improve global context to avoid niche contexts
    React.createElement(InventoryTrimCardProvider, { excludePaymentTooltipPrefix: true },
        React.createElement(UniCardWrapper, { "data-testid": "cy-trim-card" },
            React.createElement(UniCard, { componentName: "TrimCardGrid-InventoryTrimCard", events: cardEvents, onHoverBackgroundColor: backgroundColor },
                React.createElement(Box, null,
                    React.createElement(UniCard.TitleWrapper, null,
                        React.createElement(UniCard.Title, { title: `${trim.modelYear} ${trim.trimName}`, cta: cta })),
                    React.createElement(EqualHeightElement, { name: "TrimCardTransmissionSubTitle" },
                        React.createElement(UniCard.SubTitle, { subTitle: t('Pages.Bap.singleTransmissionLabel', { transmission: transmission === null || transmission === void 0 ? void 0 : transmission.transmissionName }) })),
                    React.createElement(UniCard.Pricing, { data: ((_d = trim.paymentData) === null || _d === void 0 ? void 0 : _d.data) && trim.paymentData.data[0], isFetching: trim.paymentData.isFetching, error: trim.paymentData.error, disableMsrp: false, sellingPriceFlagSource: SellingPriceFlagSource.dealer })),
                React.createElement(Optional, { when: inventoryStatusPosition === 'top' },
                    React.createElement(AvailabilityStatusBadge, { status: trim.inventoryStatus })),
                React.createElement(EqualHeightElement, { name: "TrimCardImage" },
                    React.createElement(UniCard.Image, { src: thumbnail === null || thumbnail === void 0 ? void 0 : thumbnail.src, alt: thumbnail === null || thumbnail === void 0 ? void 0 : thumbnail.alt, cta: cta })),
                React.createElement(EqualHeightElement, { name: "ExtIntSection" },
                    React.createElement(Box, { my: "m" },
                        React.createElement(ExteriorInteriorColor, { trim: trim }))),
                React.createElement(EqualHeightElement, { name: "TrimCardPayment", placeholder: !trim.trimKey },
                    React.createElement(Optional, { when: trim.trimKey },
                        React.createElement(UniCard.PaymentDetails, { payment: paymentDetailsProps, paymentTooltip: paymentTooltip, sellingPriceFlagSource: SellingPriceFlagSource.dealer }))),
                React.createElement(Optional, { when: inventoryStatusPosition === 'bottom' },
                    React.createElement(AvailabilityStatusBadge, { status: trim.inventoryStatus })),
                React.createElement(UniCard.CTA, { styles: { margin: 'auto auto 0', width: '100%', paddingTop: '16px' }, cta: cta })))));
};
export default InventoryTrimCard;
